import React, { useEffect, useMemo, useState } from 'react';
import { ChevronDown, FileType, Image as ImageIcon, Loader2, X } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { usePatient } from '../contexts/PatientContext';
import { type Patient, type PatientFile } from '../contexts/types';
import {
  isImagePatientFile,
  isPdfPatientFile,
  normalizePatientFiles,
  patientFileToDataUrl,
} from '../services/patientFiles';

type PatientFilePickerModalProps = {
  open: boolean;
  title: string;
  subtitle?: string;
  initialPatientId?: string;
  allowPdf?: boolean;
  onClose: () => void;
  onSelectFile: (payload: { patient: Patient; file: PatientFile; dataUrl: string }) => Promise<void> | void;
};

const getLocale = (language: string) => {
  if (language === 'en') return 'en-US';
  if (language === 'es') return 'es-ES';
  return 'pt-BR';
};

const formatFileDate = (value: string, language: string) => {
  const parsed = Date.parse(value);
  if (Number.isNaN(parsed)) return value;

  return new Intl.DateTimeFormat(getLocale(language), {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(parsed);
};

const PatientFilePickerModal: React.FC<PatientFilePickerModalProps> = ({
  open,
  title,
  subtitle,
  initialPatientId,
  allowPdf = false,
  onClose,
  onSelectFile,
}) => {
  const { t, language } = useLanguage();
  const { patients, activePatient, loadingPatients } = usePatient();
  const [selectedPatientId, setSelectedPatientId] = useState('');
  const [loadingFileId, setLoadingFileId] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) {
      setLoadingFileId('');
      setError('');
      return;
    }

    const preferredId =
      (initialPatientId && patients.some((patient) => patient.id === initialPatientId) ? initialPatientId : '') ||
      (activePatient?.id && patients.some((patient) => patient.id === activePatient.id) ? activePatient.id : '') ||
      patients[0]?.id ||
      '';

    setSelectedPatientId((current) => {
      if (current && patients.some((patient) => patient.id === current)) {
        return current;
      }

      return preferredId;
    });
  }, [activePatient?.id, initialPatientId, open, patients]);

  const selectedPatient = useMemo(
    () => patients.find((patient) => patient.id === selectedPatientId) || null,
    [patients, selectedPatientId]
  );

  const compatibleFiles = useMemo(() => {
    if (!selectedPatient) return [];

    const files = normalizePatientFiles(
      selectedPatient.patientFiles,
      selectedPatient.images,
      selectedPatient.createdAt
    );

    return files.filter((file) => {
      const imageMatch = isImagePatientFile(file);
      const pdfMatch = isPdfPatientFile(file);
      return allowPdf ? (imageMatch || pdfMatch) : imageMatch;
    });
  }, [allowPdf, selectedPatient]);

  const handleSelectFile = async (file: PatientFile) => {
    if (!selectedPatient) return;

    setError('');
    setLoadingFileId(file.id);

    try {
      const dataUrl = await patientFileToDataUrl(file);
      await onSelectFile({ patient: selectedPatient, file, dataUrl });
      onClose();
    } catch (error) {
      console.error('[PatientFilePickerModal][file_fetch_failed]', {
        url: file.url,
        mimeType: file.contentType || file.type,
        error,
      });
      setError('Não foi possível carregar este arquivo. Tente reenviar ou selecione outro.');
    } finally {
      setLoadingFileId('');
    }
  };
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[140] flex items-center justify-center bg-slate-950/80 px-4 py-6 backdrop-blur-sm">
      <div className="w-full max-w-5xl overflow-hidden rounded-3xl border border-slate-700 bg-slate-950 shadow-2xl">
        <div className="flex items-start justify-between gap-4 border-b border-slate-800 px-6 py-5">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[0.28em] text-blue-300/70">{t('patientFiles')}</p>
            <h3 className="mt-1 text-2xl font-bold text-white">{title}</h3>
            <p className="mt-2 text-sm text-slate-400">{subtitle || t('choosePatientFileSubtitle')}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full border border-slate-700 bg-slate-900 p-2 text-slate-400 transition-colors hover:bg-slate-800 hover:text-white"
            aria-label={title}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="grid gap-5 px-6 py-6 lg:grid-cols-[320px_1fr]">
          <div className="space-y-4">
            <div className="rounded-2xl border border-slate-800 bg-slate-900/70 p-4">
              <label className="mb-2 block text-[10px] font-bold uppercase tracking-widest text-slate-500">
                {t('consultSelectPatientTitle')}
              </label>
              {patients.length > 0 ? (
                <div className="relative">
                  <select
                    value={selectedPatientId}
                    onChange={(event) => {
                      setSelectedPatientId(event.target.value);
                      setError('');
                    }}
                    className="w-full appearance-none rounded-xl border border-slate-700 bg-slate-950/80 px-4 py-3 pr-10 text-sm text-white outline-none transition-colors focus:border-blue-500"
                  >
                    <option value="">{t('consultSelectPatientPlaceholder')}</option>
                    {patients.map((patient) => (
                      <option key={patient.id} value={patient.id}>
                        {patient.name} - {patient.anatomy}
                      </option>
                    ))}
                  </select>
                  <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
                </div>
              ) : (
                <p className="text-xs text-amber-400">{t('consultNoPatientsAvailable')}</p>
              )}
              <p className="mt-2 text-xs text-slate-500">{subtitle || t('choosePatientFileSubtitle')}</p>
            </div>

            {selectedPatient && (
              <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-4 text-sm text-slate-300">
                <p className="text-[10px] font-bold uppercase tracking-[0.24em] text-slate-500">{selectedPatient.name}</p>
                <p className="mt-2 text-xs text-slate-400">
                  {selectedPatient.anatomy} • {compatibleFiles.length} {t('patientFiles')}
                </p>
                <p className="mt-2 text-xs text-slate-500">{selectedPatient.status}</p>
              </div>
            )}
          </div>

          <div className="rounded-2xl border border-slate-800 bg-slate-900/50 p-4">
            <div className="mb-4 flex items-center justify-between gap-3">
              <div>
                <h4 className="text-sm font-bold uppercase tracking-widest text-white">{t('patientFiles')}</h4>
                <p className="mt-1 text-xs text-slate-500">{compatibleFiles.length} arquivo(s) compatíveis</p>
              </div>
              {loadingPatients && <Loader2 className="h-4 w-4 animate-spin text-blue-400" />}
            </div>

            {!selectedPatient ? (
              <div className="flex min-h-[260px] items-center justify-center rounded-2xl border border-dashed border-slate-800 bg-slate-950/50 px-6 text-center text-slate-500">
                <div className="space-y-2">
                  <ImageIcon className="mx-auto h-10 w-10 opacity-30" />
                  <p className="text-sm font-semibold">{t('consultSelectPatientTitle')}</p>
                  <p className="text-xs">{t('choosePatientFileSubtitle')}</p>
                </div>
              </div>
            ) : compatibleFiles.length === 0 ? (
              <div className="flex min-h-[260px] items-center justify-center rounded-2xl border border-dashed border-slate-800 bg-slate-950/50 px-6 text-center text-slate-500">
                <div className="space-y-2">
                  <FileType className="mx-auto h-10 w-10 opacity-30" />
                  <p className="text-sm font-semibold">{t('patientFiles')}</p>
                  <p className="text-xs">{t('patientNoArchivedImages')}</p>
                </div>
              </div>
            ) : (
              <div className="max-h-[60vh] overflow-y-auto pr-1">
                <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
                  {compatibleFiles.map((file) => {
                    const isImage = isImagePatientFile(file);
                    const isPdf = isPdfPatientFile(file);
                    const isLoading = loadingFileId === file.id;

                    return (
                      <button
                        key={file.id}
                        type="button"
                        disabled={isLoading}
                        onClick={() => handleSelectFile(file)}
                        className="group overflow-hidden rounded-2xl border border-slate-800 bg-slate-950/70 text-left transition-all hover:border-blue-500/40 hover:bg-slate-900 disabled:cursor-wait disabled:opacity-70"
                      >
                        <div className="relative aspect-[4/3] bg-black">
                          {isImage ? (
                            <img
                              src={file.url}
                              alt={file.name}
                              className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                            />
                          ) : (
                            <div className="flex h-full flex-col items-center justify-center gap-2 bg-slate-900 text-slate-300">
                              <FileType className="h-10 w-10 text-slate-400" />
                              <span className="text-[10px] font-bold uppercase tracking-widest">
                                {isPdf ? 'PDF' : file.contentType || file.type || t('patientFiles')}
                              </span>
                            </div>
                          )}

                          {isLoading && (
                            <div className="absolute inset-0 flex items-center justify-center bg-black/50 backdrop-blur-sm">
                              <Loader2 className="h-6 w-6 animate-spin text-white" />
                            </div>
                          )}
                        </div>

                        <div className="space-y-1 p-3">
                          <p className="line-clamp-2 text-sm font-semibold text-white">{file.name}</p>
                          <p className="text-[11px] uppercase tracking-[0.18em] text-slate-500">
                            {file.contentType || file.type || t('patientFiles')}
                          </p>
                          <p className="text-[11px] text-slate-400">{formatFileDate(file.createdAt, language)}</p>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {error && <p className="mt-4 text-xs text-red-400">{error}</p>}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PatientFilePickerModal;
